import React, { useEffect, useState, useCallback, useRef, MutableRefObject } from "react";
import { Button, Card, CardContent, withStyles } from "@material-ui/core";
import { getRandomNumber } from "../../../shared/utils/sharedUtils";
import "./ProgressStep.scss";
import ProgressIndicator from "./ProgressIndicators";
import InfoIcon from "@material-ui/icons/Info";
import CheckCircle from "@material-ui/icons/CheckCircle";
import Cancel from "@material-ui/icons/Cancel";
import moment from "moment-timezone";
import NoDataFound from "../../../shared/NoDataFoundComponent/NoDataFound";
import { useDispatch, useSelector } from "react-redux";
import ProgressStepSkeleton from "./ProgressStepSkeleton";
import allActions from "../../../../redux/actions";
import { getCookieValue } from "../../../../cookieHelper";
import { GrammarObject, Section, Subject, Test } from "./ProgressStepInterface";
import generatePDF, { Resolution, Margin } from "react-to-pdf";
const ColorButton = withStyles(() => ({
  root: {
    color: "#000000",
    backgroundColor: "#ffffff",
    fontWeight: 400,
    fontSize: "18px",
    "&:hover": {
      backgroundColor: "#ffffff",
    },
  },
}))(Button);

const ProgressStep = () => {
  const dispatch = useDispatch();
  const { data } = useSelector((state: any) => state.jwkPublicKey);
  const { stepProgressResultIsLoading, stepProgressResultData, stepProgressResultIsFailed } = useSelector(
    (state: any) => state.progressDetails,
  );
  const [sectionResult, setSectionResult] = useState<GrammarObject[] | null>(null);
  const [progressData, setProgressData] = useState<Test | null>(null);
  const pdfRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false);
  const[stepPlusUser,setStepPlusUser]=useState<boolean>(false)

  useEffect(() => {
    if (data) {
      getStepProgressResult();
    }
  }, [data]);

  const getStepProgressResult = useCallback(async () => {
    const getUserToken = getCookieValue(`${process.env.REACT_APP_ENV}IdToken`);
    if (getUserToken && !stepProgressResultData && !stepProgressResultIsFailed && !stepProgressResultIsLoading) {
      dispatch(allActions.progressDetailsAction.stepAuthorizationApiCall(getUserToken));
    }
    setStepPlusUser(location.pathname.includes('6'))
  }, []);

  useEffect(() => {
    if (stepProgressResultData) {
      const sectionResult: GrammarObject[] = [
        ...stepProgressResultData.sectionResult.map((_path: Section, index: number) => {
          return {
            ..._path,
            ...stepProgressResultData.test.sectionScores.filter((nes: Section, index1: number) => index == index1)[0],
          };
        }),
      ];
      setSectionResult(sectionResult);
      setProgressData(stepProgressResultData.test);
    }
  }, [stepProgressResultData]);

  const getFileName = () => {
    const currentDate = new Date();
    const timestamp = currentDate.getTime();
    return `STEP_Result_${timestamp}`;
  };

  const options = {
    resolution: Resolution.HIGH,
    filename: getFileName(),
    page: {
      margin: Margin.LARGE,
    },
    canvas: {
      qualityRatio: 1,
    },
    overrides: {
      pdf: {
        compress: true,
      },
      canvas: {
        useCORS: true,
      },
    },
  };
  const handleDownload = () => {
    setIsPdfLoading(true);
    generatePDF(pdfRef, options);
    setTimeout(() => {
      setIsPdfLoading(false);
    }, 2000);
  };
  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {sectionResult && progressData ? (
        <div className="stepresult" data-testid='stepresult'>
          <div ref={pdfRef} id={"content-to-print"}>
            <div className="stepresult-userdetails">
              <div className="stepresult-userdetails-heading">One Assessment ({stepPlusUser ? 'STEP+':'STEP'})</div>
              <div className="stepresult-userdetails-text">Test Results: {progressData.userName}</div>
              <div className="stepresult-userdetails-text">
                Date of Test: {moment(progressData.timeCompleted).format("MMMM DD, YYYY")}
              </div>
            </div>
            <div className="stepresult-testdetails">
              <div className="stepresult-testdetails-block">
                Your score places you in{" "}
                <span className="stepresult-testdetails-level">Level {progressData.level}</span>
              </div>
              <div className="stepresult-testdetails-block">
                You scored {progressData.totalScore} out of a total possible {stepPlusUser ? '1581':'787'} points.
              </div>
            </div>
            <div className="stepresult-levelinfo">{progressData.levelDetail}</div>
            <div className="stepresult-levelnote">
              <InfoIcon />
              Note: Your Common European Framework (CEFR) equivalent level is {progressData.cefr}.
            </div>
            <div className="stepresult-card">
              <Card>
                <CardContent>
                  <h4 className="stepresult-heading">Test Results</h4>
                  {sectionResult.map((section: GrammarObject, index: number) => {
                    return (
                      <div key={getRandomNumber(index)}>
                        <h6 className="stepresult-subheading">{section.name}</h6>
                        <ProgressIndicator sectionName={section.name} sectionLevel={section.level} />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>
            <div className="stepresult-section">
              {sectionResult.map((section: GrammarObject, index: number) => {
                return (
                  <div className="stepresult-section-card" key={getRandomNumber(index)}>
                    <Card>
                      <CardContent>
                        <h4 className="stepresult-section-heading">{section.name}</h4>
                        {section.subjects.map((subject: Subject, index: number) => {
                          return (
                            <div key={getRandomNumber(index)}>
                              <h6 className="stepresult-section-subheading">
                                {subject.type.replace(/<\/?donottrans>/g, "").replace(/<\/?i>/g, "")}
                              </h6>
                              <ProgressIndicator sectionName={section.name} sectionLevel={subject.level} />
                            </div>
                          );
                        })}
                        <div className="stepresult-section-reportsucess">
                          {section.well.length > 0 && (
                            <p>
                              <span className="stepresult-green">
                                <CheckCircle />
                              </span>
                              <span>
                                You did particularly well on:{" "}
                                <strong>
                                  {section.well
                                    .map((item: string) => item.replace(/<\/?donottrans>/g, "").replace(/<\/?i>/g, ""))
                                    .join(", ")}
                                </strong>
                              </span>
                            </p>
                          )}
                          {section.focus.length > 0 && (
                            <p>
                              <span className="stepresult-red">
                                <Cancel />
                              </span>
                              <span>
                                You had problems with:{" "}
                                <strong>
                                  {section.focus
                                    .map((item: string) => item.replace(/<\/?donottrans>/g, "").replace(/<\/?i>/g, ""))
                                    .join(", ")}
                                </strong>
                              </span>
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="stepresult-buttongroup">
            <ColorButton
              variant="contained"
              onClick={handleDownload}
              disabled={isPdfLoading}
              data-testid="downloadButton"
            >
              {" "}
              {isPdfLoading ? "Loading ..." : "Download"}{" "}
            </ColorButton>
            <ColorButton variant="contained" onClick={handlePrint} data-testid='printButton'>
              Print
            </ColorButton>
          </div>
        </div>
      ) : (
        stepProgressResultIsLoading && <ProgressStepSkeleton />
      )}
      {!stepProgressResultIsLoading && stepProgressResultIsFailed && <NoDataFound notProgressPage={true} />}
    </>
  );
};

export default ProgressStep;
